import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency, RATES } from "@/contexts/CurrencyContext";
import { useFavorites } from "@/contexts/FavoritesContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sparkles, User, LayoutDashboard, ShieldCheck, LogOut, CalendarRange, Crown, Heart, Menu, X } from "lucide-react";
import BecomeProviderConfirm from "./BecomeProviderConfirm";
import NotificationBell from "./NotificationBell";
import { useSystemFlags } from "@/contexts/SystemFlagsContext";

export default function Header() {
  const { user, logout } = useAuth();
  const { currency, setCurrency, symbol } = useCurrency();
  const { count: favCount } = useFavorites();
  const { flags } = useSystemFlags();
  const hangoutsEnabled = flags?.hangouts_enabled !== false;
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  // Open the one-time warning before sending the user to /become-provider.
  // Skipped if they already have a provider profile — they're just navigating
  // to their dashboard at that point.
  const goBecomeProvider = (e) => {
    if (e) e.preventDefault();
    if (user?.has_provider_profile) {
      navigate("/become-provider");
      return;
    }
    setConfirmOpen(true);
  };

  // Close the mobile menu whenever the route changes
  React.useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header
      className="glass sticky top-0 z-40 border-b border-stone-200/70"
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link
          to={user ? "/discovery" : "/auth"}
          className="flex items-center gap-2 group"
          data-testid="header-logo"
        >
          <img
            src="/logo-mark.png"
            alt="COMPANIN"
            className="h-10 w-10 rounded-2xl shadow-sm group-hover:scale-105 transition-transform"
          />
          <div className="leading-tight">
            <div className="font-display font-semibold text-stone-900 text-lg">COMPANIN</div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500 -mt-0.5">
              Booking Platform
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <Link
            to="/discovery"
            className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
              pathname.startsWith("/discovery")
                ? "bg-stone-100 text-stone-900"
                : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
            }`}
            data-testid="nav-discovery"
          >
            Discover
          </Link>
          {isAdmin && (
            <Link
              to="/admin"
              className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                pathname.startsWith("/admin")
                  ? "bg-stone-100 text-stone-900"
                  : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
              }`}
              data-testid="nav-admin"
            >
              Admin panel
            </Link>
          )}
          {!isAdmin && hangoutsEnabled && (
            <Link
              to="/group-sessions"
            className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
              pathname.startsWith("/group-sessions")
                ? "bg-stone-100 text-stone-900"
                : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
            }`}
              data-testid="nav-hangouts"
            >
              Hangouts
            </Link>
          )}
          {!isAdmin && (
            <Link
              to="/favorites"
              className={`relative px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                pathname.startsWith("/favorites")
                  ? "bg-stone-100 text-stone-900"
                  : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
              }`}
              data-testid="nav-favorites"
            >
              <Heart className={`h-4 w-4 inline-block mr-1 ${favCount > 0 ? "fill-rose-500 text-rose-500" : ""}`} strokeWidth={2.2} />
              <span className="hidden md:inline">Favourites</span>
              {favCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold">
                  {favCount}
                </span>
              )}
            </Link>
          )}
          {user && !isAdmin && (
            <>
              <Link
                to="/bookings"
                className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                  pathname.startsWith("/bookings")
                    ? "bg-stone-100 text-stone-900"
                    : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
                }`}
                data-testid="nav-bookings"
              >
                Bookings
              </Link>
              <Link
                to="/subscription"
                className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                  pathname === "/subscription"
                    ? "bg-stone-100 text-stone-900"
                    : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
                }`}
                data-testid="nav-subscription"
              >
                Premium
              </Link>
            </>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {/* Currency selector — desktop visible, mobile lives inside user dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="hidden sm:inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-stone-200 bg-white text-sm font-medium text-stone-700 hover:bg-stone-50 transition"
                data-testid="currency-selector-button"
              >
                <span className="text-stone-400">{symbol}</span>
                <span>{currency}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="currency-menu">
              <DropdownMenuLabel className="text-xs text-stone-500">Display currency</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup value={currency} onValueChange={setCurrency}>
                {Object.entries(RATES).map(([code, info]) => (
                  <DropdownMenuRadioItem
                    key={code}
                    value={code}
                    data-testid={`currency-option-${code}`}
                  >
                    <span className="mr-2 text-stone-400 w-6 inline-block text-right">{info.symbol}</span>
                    <span className="font-medium mr-2">{code}</span>
                    <span className="text-xs text-stone-500">{info.name}</span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mobile-only hamburger toggle */}
          <button
            onClick={() => setMobileOpen((o) => !o)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-stone-200 bg-white text-stone-700 hover:bg-stone-50 transition"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          {/* Mobile-only currency icon */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-full border border-stone-200 bg-white text-stone-700 hover:bg-stone-50 transition text-sm font-bold"
                data-testid="currency-selector-button-mobile"
                aria-label="Change currency"
              >
                {symbol}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs text-stone-500">Display currency</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup value={currency} onValueChange={setCurrency}>
                {Object.entries(RATES).map(([code, info]) => (
                  <DropdownMenuRadioItem
                    key={code}
                    value={code}
                    data-testid={`currency-option-mobile-${code}`}
                  >
                    <span className="mr-2 text-stone-400 w-6 inline-block text-right">{info.symbol}</span>
                    <span className="font-medium mr-2">{code}</span>
                    <span className="text-xs text-stone-500">{info.name}</span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {!user ? (
            <>
              <button
                onClick={goBecomeProvider}
                className="hidden md:inline-flex px-4 py-2 rounded-full text-sm font-medium text-stone-700 hover:bg-stone-100 transition-colors"
                data-testid="header-become-companion"
              >
                Become a companion
              </button>
              <Button
                onClick={() => navigate("/auth")}
                className="rounded-full bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white px-5"
                data-testid="header-signin-button"
              >
                Sign in
              </Button>
            </>
          ) : (
            <>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-stone-100 transition"
                  data-testid="header-user-menu"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.picture || undefined} alt={user.name} />
                    <AvatarFallback className="bg-[#4A5D4E] text-white text-xs">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium text-stone-800 max-w-[140px] truncate">
                    {user.name}
                  </span>
                  {user.is_premium && (
                    <Crown className="h-4 w-4 text-amber-500" strokeWidth={2} />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-stone-500 truncate">
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {!isAdmin && (
                  <>
                    <DropdownMenuItem
                      onClick={() => navigate("/profile")}
                      data-testid="menu-profile"
                    >
                      <User className="h-4 w-4 mr-2" /> Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/bookings")}
                      data-testid="menu-bookings"
                    >
                      <CalendarRange className="h-4 w-4 mr-2" /> My bookings
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={goBecomeProvider}
                      data-testid="menu-become-provider"
                    >
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      {user.has_provider_profile ? "Dashboard" : "Become a provider"}
                    </DropdownMenuItem>
                    {!user.has_provider_profile && (
                      <DropdownMenuItem
                        onClick={() => navigate("/become-provider/trial")}
                        data-testid="menu-provider-trial"
                        className="bg-amber-50/50 focus:bg-amber-100"
                      >
                        <Sparkles className="h-4 w-4 mr-2 text-amber-600" />
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-semibold text-stone-900">Try being a companion</span>
                          <span className="text-[10px] text-amber-800">7 days free · no bank details</span>
                        </div>
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                {isAdmin && (
                  <DropdownMenuItem
                    onClick={() => navigate("/admin")}
                    data-testid="menu-admin"
                  >
                    <ShieldCheck className="h-4 w-4 mr-2" /> Admin panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    // Navigate FIRST so ProtectedRoute doesn't intercept and
                    // stash the current (soon-to-be-forbidden) path as `?next=`
                    // — that was pushing users back to the profile page after
                    // re-login instead of landing on the home feed.
                    navigate("/discovery", { replace: true });
                    await logout();
                  }}
                  className="text-rose-600 focus:text-rose-700"
                  data-testid="menu-logout"
                >
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          )}
        </div>
      </div>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <div
          className="md:hidden border-t border-stone-200 bg-white"
          data-testid="mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col">
            {(isAdmin
              ? [
                  { to: "/discovery", label: "Discover", icon: Sparkles },
                  { to: "/admin", label: "Admin panel", icon: ShieldCheck },
                ]
              : [
              { to: "/discovery", label: "Discover", icon: Sparkles },
              ...(hangoutsEnabled
                ? [{ to: "/group-sessions", label: "Hangouts", icon: CalendarRange }]
                : []),
              {
                to: "/favorites",
                label: "Favourites",
                icon: Heart,
                badge: favCount > 0 ? favCount : null,
              },
              ...(user
                ? [
                    { to: "/bookings", label: "Bookings", icon: CalendarRange },
                    { to: "/subscription", label: "Premium", icon: Crown },
                    { to: "/profile", label: "Profile", icon: User },
                  ]
                : []),
              ...(user?.has_provider_profile
                ? [
                    {
                      to: "/provider/dashboard",
                      label: "Dashboard",
                      icon: LayoutDashboard,
                    },
                  ]
                : []),
            ]).map(({ to, label, icon: Icon, badge }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-base font-medium transition-colors ${
                  pathname.startsWith(to)
                    ? "bg-stone-100 text-stone-900"
                    : "text-stone-700 hover:bg-stone-50"
                }`}
                data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className="h-5 w-5 shrink-0" strokeWidth={2.0} />
                <span className="flex-1">{label}</span>
                {badge && (
                  <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold">
                    {badge}
                  </span>
                )}
              </Link>
            ))}

            {!user && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Link to="/auth" onClick={() => setMobileOpen(false)}>
                  <Button variant="outline" className="w-full rounded-full border-stone-300 h-11">
                    Sign in
                  </Button>
                </Link>
                <Link to="/auth?mode=register" onClick={() => setMobileOpen(false)}>
                  <Button className="w-full rounded-full bg-[#4A5D4E] hover:bg-[#3B4A3E] h-11">
                    Get started
                  </Button>
                </Link>
              </div>
            )}

            {user && (
              <button
                onClick={async () => {
                  setMobileOpen(false);
                  // Navigate first — see desktop logout comment above.
                  navigate("/discovery", { replace: true });
                  await logout();
                }}
                className="mt-2 flex items-center gap-3 px-4 py-3 rounded-2xl text-base font-medium text-rose-700 hover:bg-rose-50"
                data-testid="mobile-nav-logout"
              >
                <LogOut className="h-5 w-5 shrink-0" /> Log out
              </button>
            )}
          </div>
        </div>
      )}

      <BecomeProviderConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={() => {
          setConfirmOpen(false);
          setMobileOpen(false);
          navigate("/become-provider");
        }}
      />
    </header>
  );
}
